import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

/**
 * Audit Logger Service
 * Provides comprehensive logging for all system operations
 * Tracks CRUD operations, user actions, and system events
 */
class AuditLogger {
  /**
   * Core logging method
   * @param {string} action - The action performed (CREATE, UPDATE, DELETE, VIEW)
   * @param {string} resource - The resource type (sales, users, system)
   * @param {string} resourceId - The ID of the resource
   * @param {object} changes - Object containing before/after data
   * @param {object} metadata - Additional metadata
   * @returns {Promise<string>} - Returns the log entry ID
   */
  static async log(action, resource, resourceId, changes = {}, metadata = {}) {
    try {
      // Get current user info (this will be enhanced with proper auth)
      const currentUser = this.getCurrentUser();
      
      // Calculate severity based on action and changes
      const severity = this.calculateSeverity(action, resource, changes);
      
      // Prepare log entry
      const logEntry = {
        timestamp: serverTimestamp(),
        userId: currentUser.id || 'anonymous',
        userEmail: currentUser.email || 'unknown@unknown.com',
        action: action.toUpperCase(),
        resource: resource.toLowerCase(),
        resourceId: resourceId || null,
        changes: this.sanitizeChanges(changes),
        metadata: {
          userAgent: navigator.userAgent,
          source: 'web',
          sessionId: this.getSessionId(),
          ...metadata
        },
        severity: severity,
        tags: this.generateTags(action, resource, changes)
      };

      // Add to Firestore with retry logic
      const docRef = await this.addDocWithRetry(logEntry, 2);
      
      // Track write for cost monitoring
      const estimatedSize = JSON.stringify(logEntry).length;
      this.trackWrite(estimatedSize);
      
      console.log(`✅ Audit log created: ${action} ${resource} ${resourceId}`, {
        logId: docRef.id,
        action,
        resource,
        resourceId,
        size: `${estimatedSize} bytes`
      });
      
      // Log usage report every 100 writes
      if (this.usageMetrics.dailyWrites % 100 === 0) {
        this.logUsageReport();
      }
      
      return docRef.id;
    } catch (error) {
      console.error('❌ Failed to create audit log:', error);
      // Don't throw error to avoid breaking main functionality
      this.logToConsole(action, resource, resourceId, changes, error);
      return null;
    }
  }

  /**
   * Add document to Firestore with retry logic
   */
  static async addDocWithRetry(logEntry, maxRetries = 2) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await addDoc(collection(db, 'audit_logs'), logEntry);
      } catch (error) {
        lastError = error;
        
        // Don't retry for quota exceeded errors
        if (error.code === 'resource-exhausted') {
          throw error;
        }
        
        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
    
    throw lastError;
  }

  /**
   * Log sales-specific changes
   * @param {string} action - CREATE, UPDATE, DELETE
   * @param {string} saleId - The sale ID
   * @param {object} beforeData - Data before change
   * @param {object} afterData - Data after change
   * @param {object} metadata - Additional metadata
   */
  static async logSalesChange(action, saleId, beforeData = {}, afterData = {}, metadata = {}) {
    const changes = {
      before: beforeData,
      after: afterData,
      fieldsChanged: this.getChangedFields(beforeData, afterData)
    };

    // Add sales-specific metadata
    const salesMetadata = {
      ...metadata,
      priceChange: this.calculatePriceChange(beforeData, afterData),
      statusChange: this.getStatusChange(beforeData, afterData),
      clientInfo: afterData?.clientName || beforeData?.clientName || 'Unknown'
    };

    return await this.log(action, 'sales', saleId, changes, salesMetadata);
  }

  /**
   * Log user actions (login, logout, access attempts)
   * @param {string} action - The user action
   * @param {object} details - Action details
   */
  static async logUserAction(action, details = {}) {
    const metadata = {
      ...details,
      timestamp: new Date().toISOString(),
      ipAddress: await this.getClientIP()
    };

    return await this.log(action, 'user', details.userId || 'unknown', {}, metadata);
  }

  /**
   * Log system events (errors, backups, configuration changes)
   * @param {string} event - The system event
   * @param {object} details - Event details
   */
  static async logSystemEvent(event, details = {}) {
    const metadata = {
      ...details,
      systemInfo: {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      }
    };

    return await this.log(event, 'system', 'system', {}, metadata);
  }

  // Helper Methods

  /**
   * Get current user information
   */
  static getCurrentUser() {
    const currentUser = auth.currentUser;
    
    if (currentUser) {
      return {
        id: currentUser.uid,
        email: currentUser.email || 'unknown@example.com',
        name: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User'
      };
    }
    
    // Fallback for unauthenticated users (shouldn't happen in normal operation)
    return {
      id: 'anonymous_' + Date.now(),
      email: 'anonymous@system.local',
      name: 'Anonymous User'
    };
  }

  /**
   * Calculate log severity based on action and changes
   */
  static calculateSeverity(action, resource, changes) {
    // Security-related actions
    if (action === 'FAILED_LOGIN' || action === 'UNAUTHORIZED_ACCESS') return 'HIGH';
    if (action === 'LOGIN' && resource === 'user') return 'MEDIUM';
    
    // System events
    if (action === 'ERROR' && resource === 'system') return 'CRITICAL';
    if (action === 'BACKUP' && resource === 'system') return 'MEDIUM';
    
    // Sales operations
    if (action === 'DELETE') return 'HIGH';
    if (action === 'CREATE' && resource === 'sales') return 'MEDIUM';
    if (action === 'UPDATE') {
      const fieldsChanged = changes.fieldsChanged || [];
      if (fieldsChanged.includes('price') || fieldsChanged.includes('status')) {
        return 'MEDIUM';
      }
    }
    return 'LOW';
  }

  /**
   * Sanitize sensitive data from changes
   */
  static sanitizeChanges(changes) {
    const sensitiveFields = ['password', 'token', 'apiKey', 'secret'];
    const sanitized = { ...changes };
    
    if (sanitized.before) {
      sensitiveFields.forEach(field => {
        if (sanitized.before[field]) {
          sanitized.before[field] = '***REDACTED***';
        }
      });
    }
    
    if (sanitized.after) {
      sensitiveFields.forEach(field => {
        if (sanitized.after[field]) {
          sanitized.after[field] = '***REDACTED***';
        }
      });
    }
    
    return sanitized;
  }

  /**
   * Generate tags for easier filtering
   */
  static generateTags(action, resource, changes) {
    const tags = [action.toLowerCase(), resource.toLowerCase()];
    
    if (changes.fieldsChanged) {
      changes.fieldsChanged.forEach(field => tags.push(`field:${field}`));
    }
    
    return tags;
  }

  /**
   * Get changed fields between before and after data
   */
  static getChangedFields(before, after) {
    const changedFields = [];
    const allKeys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
    
    allKeys.forEach(key => {
      const beforeValue = before ? before[key] : undefined;
      const afterValue = after ? after[key] : undefined;
      if (beforeValue !== afterValue) {
        changedFields.push(key);
      }
    });
    
    return changedFields;
  }

  /**
   * Calculate price change for sales
   */
  static calculatePriceChange(before, after) {
    const beforePrice = parseFloat(before?.price || 0);
    const afterPrice = parseFloat(after?.price || 0);
    
    if (beforePrice === afterPrice) return null;
    
    return {
      from: beforePrice,
      to: afterPrice,
      difference: afterPrice - beforePrice,
      percentChange: beforePrice > 0 ? ((afterPrice - beforePrice) / beforePrice * 100).toFixed(2) : null
    };
  }

  /**
   * Get status change information
   */
  static getStatusChange(before, after) {
    if (before?.status === after?.status) return null;
    
    return {
      from: before?.status || 'Unknown',
      to: after?.status || 'Unknown'
    };
  }

  /**
   * Get session ID (simple implementation)
   */
  static getSessionId() {
    let sessionId = sessionStorage.getItem('audit_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('audit_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Get client IP (placeholder - would need backend implementation)
   */
  static async getClientIP() {
    // This would typically be provided by the backend
    return 'client_ip_placeholder';
  }

  /**
   * Fallback console logging when Firestore fails
   */
  static logToConsole(action, resource, resourceId, changes, error) {
    console.group(`🚨 Audit Log Fallback: ${action} ${resource}`);
    console.log('Resource ID:', resourceId);
    console.log('Changes:', changes);
    console.log('Error:', error);
    console.log('Timestamp:', new Date().toISOString());
    console.groupEnd();
  }

  /**
   * Batch logging for bulk operations
   */
  static async logBulkOperation(action, resource, items, metadata = {}) {
    const bulkMetadata = {
      ...metadata,
      bulkOperation: true,
      itemCount: items.length,
      items: items.map(item => item.id || item).slice(0, 10) // Limit to first 10 IDs
    };

    return await this.log(`BULK_${action}`, resource, `bulk_${Date.now()}`, {}, bulkMetadata);
  }

  /**
   * Create a detailed log for form validation errors
   */
  static async logValidationError(resource, resourceId, validationErrors, formData) {
    const metadata = {
      validationErrors,
      formData: this.sanitizeChanges({ after: formData }).after,
      errorCount: Object.keys(validationErrors).length
    };

    return await this.log('VALIDATION_ERROR', resource, resourceId, {}, metadata);
  }

  /**
   * Cost monitoring and usage tracking
   */
  static usageMetrics = {
    dailyWrites: 0,
    dailyReads: 0,
    totalLogSize: 0,
    lastReset: new Date().toDateString()
  };

  /**
   * Track write operation for cost monitoring
   */
  static trackWrite(logSize = 600) {
    const today = new Date().toDateString();
    if (this.usageMetrics.lastReset !== today) {
      // Reset daily counters
      this.usageMetrics.dailyWrites = 0;
      this.usageMetrics.totalLogSize = 0;
      this.usageMetrics.lastReset = today;
    }
    
    this.usageMetrics.dailyWrites++;
    this.usageMetrics.totalLogSize += logSize;
  }

  /**
   * Track read operation for cost monitoring
   */
  static trackRead(readCount = 1) {
    const today = new Date().toDateString();
    if (this.usageMetrics.lastReset !== today) {
      this.usageMetrics.dailyReads = 0;
    }
    this.usageMetrics.dailyReads += readCount;
  }

  /**
   * Calculate estimated daily cost
   */
  static getUsageMetrics() {
    const writesCost = (this.usageMetrics.dailyWrites / 100000) * 0.18; // $0.18 per 100k writes
    const readsCost = (this.usageMetrics.dailyReads / 100000) * 0.036; // $0.036 per 100k reads
    const storageCost = (this.usageMetrics.totalLogSize / 1024 / 1024 / 1024) * 0.18 / 30; // $0.18 per GB per month, daily
    
    return {
      dailyWrites: this.usageMetrics.dailyWrites,
      dailyReads: this.usageMetrics.dailyReads,
      totalLogSize: this.usageMetrics.totalLogSize,
      estimatedDailyCost: writesCost + readsCost + storageCost,
      estimatedMonthlyCost: (writesCost + readsCost) * 30 + storageCost * 30,
      breakdown: {
        writes: writesCost,
        reads: readsCost,
        storage: storageCost
      }
    };
  }

  /**
   * Log usage metrics (for monitoring)
   */
  static logUsageReport() {
    const metrics = this.getUsageMetrics();
    console.group('📊 Audit System Usage Report');
    console.log('Daily Writes:', metrics.dailyWrites);
    console.log('Daily Reads:', metrics.dailyReads);
    console.log('Total Log Size:', `${(metrics.totalLogSize / 1024).toFixed(2)} KB`);
    console.log('Estimated Daily Cost:', `$${metrics.estimatedDailyCost.toFixed(6)}`);
    console.log('Estimated Monthly Cost:', `$${metrics.estimatedMonthlyCost.toFixed(4)}`);
    console.log('Cost Breakdown:', metrics.breakdown);
    console.groupEnd();
    
    return metrics;
  }

  /**
   * Enhanced log method with cost tracking
   */
  static async logWithTracking(action, resource, resourceId, changes = {}, metadata = {}) {
    const result = await this.log(action, resource, resourceId, changes, metadata);
    
    // Estimate log size for cost tracking
    const logData = {
      action, resource, resourceId, changes, metadata,
      timestamp: new Date(),
      userId: this.getCurrentUser().id
    };
    const estimatedSize = JSON.stringify(logData).length;
    
    this.trackWrite(estimatedSize);
    
    // Log usage report every 100 writes
    if (this.usageMetrics.dailyWrites % 100 === 0) {
      this.logUsageReport();
    }
    
    return result;
  }
}

export default AuditLogger;
